import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { LoadingService } from '@core/services/loading/loading.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { ProductsService } from '@core/services/products/products.service';

import { BaseProductCommand } from '@shared/commands/base-product.command';
import { IProductCommandContext } from '@shared/models/command';
import { ProductTypes } from '@shared/models/products.model';
import { CommandAvailabilityService } from '@shared/services/command-availability.service';

@Injectable({ providedIn: 'root' })
export class ProductCloseCommand extends BaseProductCommand {
  private productContext: IProductCommandContext;

  /**
   * Контекстные заголовки команды по типам продуктов (по аналогии с AboutCommand).
   * Для вклада действие называется «Закрыть вклад», для остальных продуктов — «Закрыть».
   */
  private readonly titleMap = {
    [ProductTypes.ACCOUNT]: 'Закрыть',
    [ProductTypes.CARD]: 'Закрыть',
    [ProductTypes.DEPOSIT]: 'Закрыть вклад',
    [ProductTypes.SAVE_ACCOUNT]: 'Закрыть',
    [ProductTypes.METAL_ACCOUNT]: 'Закрыть'
  } as const;

  readonly type = 'productClose';
  readonly icon = 'close-account';
  readonly productType: ProductTypes[] = [
    ProductTypes.ACCOUNT,
    ProductTypes.CARD,
    ProductTypes.DEPOSIT,
    ProductTypes.SAVE_ACCOUNT,
    ProductTypes.METAL_ACCOUNT
  ];

  readonly fallbackTitle = 'Закрыть';

  get title(): string {
    // Используем метод из BaseProductCommand
    return this.getContextualTitle(this.titleMap, this.fallbackTitle);
  }

  /**
   * Серверные настройки, определяющие доступность закрытия продукта.
   * Зависят от типа продукта: счета/металлические/накопительные -> CLOSE_ACC,
   * карты -> CLOSE_CARD, вклады -> CLOSE_CONTR.
   *
   * Примечание: читается после установки контекста в checkAdditionalAvailability(),
   * поэтому this.productContext гарантированно заполнен.
   */
  get settingsNames(): string[] {
    const productType = this.productContext?.data?.productType;
    let settings: string[];

    switch (productType) {
      case ProductTypes.ACCOUNT:
      case ProductTypes.SAVE_ACCOUNT:
      case ProductTypes.METAL_ACCOUNT:
        settings = ['REQUEST/CLOSE_ACC'];
        break;

      case ProductTypes.CARD:
        settings = ['REQUEST/CLOSE_CARD'];
        break;

      case ProductTypes.DEPOSIT:
        settings = ['REQUEST/CLOSE_CONTR'];
        break;

      default:
        settings = [];
    }

    return settings;
  }

  get order(): number {
    return this.getContextualOrder(
      {
        [ProductTypes.CARD]: 100, // Карты: Закрыть (10-й в списке "прочее")
        [ProductTypes.ACCOUNT]: 50, // Счет: основное действие (5-й в общем списке)
        [ProductTypes.SAVE_ACCOUNT]: 50, // Накопительный счет: основное действие
        [ProductTypes.DEPOSIT]: 50 // Вклад: основное действие
      },
      999
    );
  }

  // В зависимости от типа продукта, кнопка может быть либо действием, либо инфо-действием.
  // Мы устанавливаем isInfoAction на основе наиболее частого случая.
  get isInfoAction() {
    // Для карт это инфо-действие, для остальных - основное.
    return this.productContext?.data.productType === 'card';
  }

  constructor(
    commandAvailabilityService: CommandAvailabilityService,
    private productsService: ProductsService,
    private loadingService: LoadingService,
    private navigationService: NavigationService
  ) {
    super(commandAvailabilityService);
  }

  async execute(context: IProductCommandContext): Promise<void> {
    this.loadingService.showLoader(true);
    this.productsService
      .closeProduct(context.data.id)
      .pipe(finalize(() => this.loadingService.showLoader(false)))
      .subscribe((data) => {
        if (data.result) {
          const path = data.navParams.type === 'card' ? 'card-close' : 'product-close';

          this.navigationService.push(`product/${context.data.id}/${path}`, {
            state: { ...data.navParams }
          });
        } else {
          this.productsService.showProductCloseError(data.product.productType, data.product.currencyCode);
        }
      });
  }

  protected checkAdditionalAvailability(context: IProductCommandContext): Observable<boolean> | boolean {
    this.productContext = context;

    // Логика из ProductsService: не закрывать кредитные карты и проверять возможность закрытия.
    // Теперь можно дать закрыть, но заккоментирую этот кусок на всякий.
    // if (context.data.productType === 'card' && context.data.type === '5') {
    //   return false;
    // }

    if (this.productsService.checkCloseAccountPossibility(context.data)) {
      return false;
    }

    return true;
  }
}
